import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import useExternalApiService from "helpers/ExternalApiService";
import { store } from 'stores/Store';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { init, logEvent, setUserId } from '@amplitude/analytics-browser';

const AMPLITUDE_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY!;
const INIT_TIMEOUT_MS = 15000;
const AUTO_RETRY_SECONDS = 5;

export default function ProtectedRoute({ children }: any) {
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const { postData, getData } = useExternalApiService(process.env.REACT_APP_EAI_URL);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();

  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RETRY_SECONDS);

  useEffect(() => {
    const initializeUser = async () => {
      if (isAuthenticated && !store.user && !error) {
        try {
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('Timed out loading account data')),
              INIT_TIMEOUT_MS
            )
          );
          const fetches = Promise.all([
            postData('/api/get_user', {}),
            getData('/api/e/call_winback_get_user_impersonate_list')
          ]);
          const [result, profile] = await Promise.race([fetches, timeout]) as [any, any];

          store.setRefreshDataTime(profile.t_historical_version);
          store.setUser({
            userFriendlyName: `${result.user_info.first_name ?? 'Your'} ${result.user_info.last_name ?? 'Name'}`,
            userName: result.user_info.user_name,
            userPicture: result.user_info.icon_url,
            impersonateList: profile.fmc_list,
            isAdmin: profile.is_admin,
            earlyRelease: profile.early_release
          });

          if (profile.is_admin) {
            store.setImpersonatedUser(profile.fmc_list[0]);
          } else {
            store.setImpersonatedUser(result.user_info.user_name);
          }

          console.log("Setting amplitude user_name:", result.user_info.user_name);
          init(AMPLITUDE_KEY, result.user_info.user_name, {
              autocapture: {
                pageViews: false,
                elementInteractions: true,
            }
          });
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError(err as Error);
        }
      }
    };

    if (!isLoading) {
      if (isAuthenticated) {
        initializeUser();
      } else {
        loginWithRedirect({
          appState: {
            returnTo: "/",
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, loginWithRedirect, retryCount, error]);

  // Auto-retry countdown when error is set
  useEffect(() => {
    if (!error) {
      setSecondsLeft(AUTO_RETRY_SECONDS);
      return;
    }
    setSecondsLeft(AUTO_RETRY_SECONDS);
    const retryTimer = setTimeout(() => {
      setError(null);
      setRetryCount(c => c + 1);
    }, AUTO_RETRY_SECONDS * 1000);
    const tickTimer = setInterval(() => {
      setSecondsLeft(s => Math.max(0, s - 1));
    }, 1000);
    return () => {
      clearTimeout(retryTimer);
      clearInterval(tickTimer);
    };
  }, [error]);

  const handleRetryNow = () => {
    setError(null);
    setRetryCount(c => c + 1);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (error && !store.user) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        padding: 20, textAlign: 'center',
        background: 'white',
        zIndex: 1000,
      }}>
        <h2 style={{ marginBottom: 8, color: '#333' }}>Couldn't load your account</h2>
        <p style={{ marginBottom: 8, color: '#666', maxWidth: 320 }}>
          Check your internet connection.
        </p>
        <p style={{ marginBottom: 20, color: '#999', fontSize: 14 }}>
          Retrying automatically in {secondsLeft}s…
        </p>
        <button
          onClick={handleRetryNow}
          style={{
            padding: '10px 24px', fontSize: 16, cursor: 'pointer',
            borderRadius: 4, border: '1px solid #1971c2',
            background: '#1971c2', color: 'white',
            marginBottom: 12, minWidth: 160,
          }}
        >
          Retry now
        </button>
        <button
          onClick={handleLogout}
          style={{
            padding: '10px 24px', fontSize: 16, cursor: 'pointer',
            borderRadius: 4, border: '1px solid #c92a2a',
            background: '#c92a2a', color: 'white',
            minWidth: 160,
          }}
        >
          Log Out
        </button>
      </div>
    );
  }

  return (
    (isLoading || !isAuthenticated) ?
      <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div>Loading...</div>
      </div> : children
  )


}
